/**
 * DatasetUploadPage Component (TASK-288, Epic 29 — Outbound Calling)
 *
 * Schema-driven base upload. The campaign's own schema (dataset.schema, so a
 * Telegram list maps telegram/name/company/phone) drives a column-mapping
 * form rendered ONE ROW PER FIELD —
 * generic, so a custom field appears automatically. The user maps file columns
 * onto schema fields; the file + mapping are sent as multipart and the import
 * result ({created, duplicates, invalid}) is shown. A missing required-column
 * mapping is rejected client-side before the API is called.
 *
 * KAN-725: a Google Sheets link may replace the file; "Сколько загрузить" and
 * "skip existing clients" options; the result shows the dedupe summary.
 *
 * KAN-727: the form opens with the last remembered settings of this list kind
 * (mapping, limit, checkbox), offers recent sheet links, and once the file or
 * link is known turns the mapping into selects of its real headers.
 *
 * KAN-826: sections "Организация" (organization columns) and "Свои поля"
 * (custom fields), "Дополнить уже загруженных" with a preview; state lives in
 * useUploadCompanyFields / useUploadUpdateJob, the UI in components/datasets.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Download, FileText, Loader2, Upload } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { CampaignKindBadge } from '@/components/datasets/CampaignKindBadge';
import { OutcomePreview } from '@/components/datasets/OutcomePreview';
import { ColumnMappingFields } from '@/components/datasets/ColumnMappingFields';
import { useCsvTemplate } from '@/hooks/useCsvTemplate';
import { useUploadHeaders } from '@/hooks/useUploadSource';
import { useUploadPresetSetup } from '@/hooks/useUploadPresetSetup';
import { useUploadCompanyFields } from '@/hooks/useUploadCompanyFields';
import { useUploadUpdateJob } from '@/hooks/useUploadUpdateJob';
import { UploadCompanyFields } from '@/components/datasets/UploadCompanyFields';
import { UploadExtraFields } from '@/components/datasets/UploadExtraFields';
import { UploadUpdateExisting } from '@/components/datasets/UploadUpdateExisting';
import { UploadUpdateCounters } from '@/components/datasets/UploadUpdateCounters';
import {
  autoSelectMapping,
  extractMappingFieldErrors,
  mappingFromPreset,
} from '@/lib/uploadMapping';
import {
  UploadOptions,
  UploadSheetUrlInput,
  type UploadOptionsValue,
} from '@/components/datasets/UploadOptions';
import { datasetsApi } from '@/services/api/datasets';
import {
  formatUpdateSummary,
  formatUploadSummary,
  isGoogleSheetUrl,
  parseUploadLimit,
} from '@/lib/uploadSummary';
import type {
  ColumnMapping,
  SchemaField,
  UploadOptions as UploadRequestOptions,
  UploadPreset,
  UploadResult,
} from '@/types/dataset';

export function DatasetUploadPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  // KAN-850: read by the opening preset (fills its link only without a file).
  const fileRef = useRef<File | null>(null);
  fileRef.current = file;
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  // TASK-353: a selected .xlsx cannot be auto-mapped on the front (no parser),
  // so we surface a soft "map columns manually / use the template" hint instead.
  const [isXlsx, setIsXlsx] = useState(false);
  // KAN-725: Google Sheets link, row limit, skip-existing-clients flag.
  const [options, setOptions] = useState<UploadOptionsValue>({
    sheetUrl: '',
    limit: '',
    // KAN-861: on by default (owner 19.10), the user may uncheck it.
    skipExistingClients: true,
  });
  // KAN-727: per-field "column not found" errors of the last upload.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    document.title = 'Загрузка списка - SMETA AI';
  }, []);

  const { data: dataset } = useQuery({
    queryKey: ['dataset', id],
    queryFn: () => datasetsApi.get(id),
    enabled: !!id,
  });

  // The schema of the campaign itself is the primary source: profile-defaults
  // knows nothing about the kind and always answers with the voice schema.
  // It stays only as a fallback for an old campaign stored without a schema.
  const codename = dataset?.profile_codename;
  const datasetSchema = useMemo(() => dataset?.schema ?? [], [dataset]);
  const needsFallback = !!dataset && datasetSchema.length === 0;

  const { data: profileDefaults } = useQuery({
    queryKey: ['profileDefaults', codename, dataset?.kind],
    queryFn: () =>
      datasetsApi.profileDefaults(codename as string, dataset?.kind),
    enabled: needsFallback && !!codename,
  });

  const schema: SchemaField[] = useMemo(
    () =>
      datasetSchema.length > 0 ? datasetSchema : (profileDefaults?.schema ?? []),
    [profileDefaults, datasetSchema]
  );

  // TASK-413 (AC-1): a ref mirror of `schema`, read inside FileReader.onload
  // (autoMapFromCsv). The schema arrives asynchronously (useQuery) and so does
  // the FileReader read — when a file lands (via drop, or any change) before
  // the schema query resolves, the `schema` value CLOSED OVER at callback
  // creation would still be the empty initial array by the time onload fires,
  // silently producing an empty auto-map. A ref always reflects the LATEST
  // schema at the moment onload actually runs, regardless of which async
  // operation (schema fetch vs. file read) settles first.
  const schemaRef = useRef<SchemaField[]>(schema);
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);

  // TASK-353 (A/B): template + on-page example (useCsvTemplate).
  const { previewLines, downloadTemplate: handleDownloadTemplate } = useCsvTemplate(schema);

  // KAN-727: remembered settings (latest preset applied once on open).
  const { savedMapping, savedCompanyFields, savedExtraFields, applyPreset, recentSheets } =
    useUploadPresetSetup(dataset?.kind, setOptions, id, () => fileRef.current !== null);
  // KAN-826: organization columns, custom fields, "update existing" + its job.
  const companyForm = useUploadCompanyFields({ schema, savedCompanyFields, savedExtraFields });
  const updateJob = useUploadUpdateJob(id);

  // TASK-370 (AC-1): the mapping opens PREFILLED with the schema labels — the
  // very strings the template and the «Пример» block advertise (`label || name`).
  // A user who filled OUR template then has nothing to type.
  // KAN-727: the remembered mapping (if any) overrides the label defaults.
  const defaultMapping = useMemo(() => {
    const defaults: ColumnMapping = {};
    schema.forEach((field) => {
      defaults[field.name] = field.label || field.name;
    });
    return mappingFromPreset(defaults, savedMapping);
  }, [schema, savedMapping]);

  // KAN-727: real headers of the chosen file / link -> selects + auto-match.
  const headersState = useUploadHeaders(id, file, options.sheetUrl);
  const headersData = headersState.data;
  const headerNames = headersData?.headers ?? [];
  const hasHeadersRef = useRef(false);
  hasHeadersRef.current = !!headersData?.headers.length;
  const {
    autoSelect: autoSelectCompany,
    validate: validateCompanyForm,
    clearErrors: clearCompanyErrors,
    setServerErrors: setCompanyServerErrors,
    requestOptions: companyRequestOptions,
  } = companyForm;
  useEffect(() => {
    if (!headersData?.headers.length) return;
    const auto = autoSelectMapping(schemaRef.current, headersData.headers, savedMapping);
    setMapping(auto);
    autoSelectCompany(headersData.headers, auto);
  }, [headersData, savedMapping, schema, autoSelectCompany]);

  // The schema arrives asynchronously (useQuery), so the defaults cannot be the
  // useState initial value — they must be applied once the schema lands.
  // WHY the dep is [defaultMapping] and NEVER [mapping]: keying on `mapping`
  // would re-apply the defaults on every mapping change — an infinite re-render
  // loop, and a field the user deliberately cleared (AC-7) would be refilled on
  // the spot, making it impossible to empty. Keyed on the schema-derived memo,
  // the defaults are written exactly once per schema.
  useEffect(() => {
    // KAN-727: real headers already drive the mapping (autoSelectMapping).
    if (hasHeadersRef.current) return;
    setMapping(defaultMapping);
  }, [defaultMapping]);
  const uploadMutation = useMutation({
    mutationFn: (payload: {
      file: File | null;
      mapping: ColumnMapping;
      options: UploadRequestOptions;
    }) => datasetsApi.upload(id, payload.file, payload.mapping, payload.options),
    onSuccess: (data, variables) => {
      setError(null);
      // KAN-826: "update existing" / its preview run as a background job; stay
      // on the page and follow it (useUploadUpdateJob).
      if (data.job_id) {
        const preview = data.dry_run ?? !!variables.options.dryRun;
        // A run imported the new rows before the job: its stats go to the job panel.
        updateJob.start(data.job_id, preview ? 'preview' : 'run', preview ? null : data);
        return;
      }
      // Keep the on-page panels working (green success / amber 0-import warning).
      setResult(data);

      // Backend returns both naming variants; be tolerant of either.
      const added = data.created ?? 0;
      const duplicates = data.duplicates ?? 0;
      const invalid = data.invalid ?? 0;
      const skippedClients = data.skipped_existing_clients ?? 0;

      // TASK-356: redirect back to the campaign (so the user cannot re-submit and
      // create duplicates) whenever the file did its job — either something was
      // added, or everything was an already-known duplicate with NOTHING to fix.
      // Stay on the form (amber warning, TASK-338) only when there are invalid
      // rows to fix, or nothing landed at all (a real failure to investigate).
      const shouldRedirect =
        added > 0 ||
        (added === 0 && invalid === 0 && duplicates + skippedClients > 0) ||
        (invalid === 0 && (data.updated_records ?? 0) > 0);

      if (shouldRedirect) {
        // KAN-725: the toast carries the full dedupe summary; invalid rows
        // that silently didn't import are appended (M1).
        const updates = formatUpdateSummary(data);
        const summary = updates
          ? `${formatUploadSummary(data)}. ${updates}`
          : formatUploadSummary(data);
        const message = invalid > 0 ? `${summary}. Ошибки: ${invalid}` : summary;
        // invalid > 0 → not everything landed: warn (amber) instead of success.
        if (invalid > 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
        navigate(`/clients/cold-calls/${id}`);
      }
    },
    onError: (err: unknown) => {
      setError(extractErrorMessage(err, 'Не удалось загрузить базу'));
      setFieldErrors(extractMappingFieldErrors(err));
      setCompanyServerErrors(err);
    },
  });

  const handleMappingChange = useCallback((fieldName: string, value: string) => {
    setMapping((prev) => ({ ...prev, [fieldName]: value }));
    setFieldErrors((prev) => {
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // KAN-727: a picked recent link replaces the file and brings its settings.
  const handlePickRecent = useCallback(
    (preset: UploadPreset) => {
      setFile(null);
      setIsXlsx(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      applyPreset(preset, preset.sheet_url);
    },
    [applyPreset]
  );

  // TASK-353 (C): read the CSV's first line on the front (FileReader, no xlsx
  // library), split by comma/semicolon, and prefill the mapping for any header
  // that matches a schema field by label OR name (case-insensitive, trimmed).
  const autoMapFromCsv = useCallback(
    (selected: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        // KAN-727: real headers from the server already drive the mapping.
        if (hasHeadersRef.current) return;
        const text = String(reader.result ?? '');
        const firstLine = text.split(/\r?\n/)[0] ?? '';
        if (!firstLine.trim()) return;
        const headers = firstLine
          .split(/[,;]/)
          .map((h) => h.trim())
          .filter(Boolean);
        const auto: ColumnMapping = {};
        // Read via the ref (see schemaRef above) — NOT the closed-over
        // `schema` — so a schema that finishes loading AFTER this read
        // started is still picked up correctly.
        schemaRef.current.forEach((field) => {
          const match = headers.find((h) => {
            const hl = h.toLowerCase();
            return (
              hl === field.label.trim().toLowerCase() ||
              hl === field.name.trim().toLowerCase()
            );
          });
          if (match) {
            auto[field.name] = match;
          }
        });
        // TASK-370 (AC-2/AC-6): REPLACE the mapping instead of merging over it,
        // so a schema field the file has no column for ends up EMPTY rather than
        // keeping its default. We parsed this CSV — we KNOW its columns. Leaving
        // «Телефон» in a field whose column provably isn't in the file lies to
        // the user and, worse, sails past the required-column guard below: the
        // backend then imports 0 rows instead of the page failing instantly.
        // The empty `auto` case is deliberately NOT guarded: a CSV matching
        // nothing must clear the defaults, not preserve them.
        setMapping(auto);
      };
      reader.readAsText(selected);
    },
    []
  );

  // TASK-413 (AC-1): the single path both the native <input onChange> and the
  // DropZone's onDrop funnel through — file selection must behave IDENTICALLY
  // regardless of how the file arrived (validation, mapping reset/auto-map,
  // isXlsx flag). `selected === null` means "selection cleared" (input) and is
  // always accepted; a non-null file is rejected up front when its extension
  // isn't one the backend can parse, so an unsupported drop never reaches
  // `datasetsApi.upload` and never silently overwrites the current file/mapping.
  const applySelectedFile = useCallback(
    (selected: File | null) => {
      if (selected) {
        const lower = selected.name.toLowerCase();
        const isSupported = lower.endsWith('.csv') || lower.endsWith('.xlsx');
        if (!isSupported) {
          setError(
            'Неподдерживаемый формат файла. Допустимы только .csv и .xlsx.'
          );
          return;
        }
      }

      setFile(selected);
      setResult(null);
      setError(null);
      setFieldErrors({});
      // Reset the mapping on every new selection so auto-mapped columns from a
      // PREVIOUS file never survive into this one (they'd point at columns that
      // don't exist in the new file → silent duplicates/invalids on import).
      // TASK-370 (AC-4): reset lands on the DEFAULTS, not on {} — we know
      // nothing about the new file yet, so the schema labels are the best guess
      // (and for .xlsx, where no parser runs, they are what the user keeps).
      // A parsed CSV overwrites this a moment later in autoMapFromCsv.
      setMapping(defaultMapping);
      if (!selected) {
        setIsXlsx(false);
        return;
      }
      const isXlsxFile = selected.name.toLowerCase().endsWith('.xlsx');
      setIsXlsx(isXlsxFile);
      // XLSX has no front-end parser → skip auto-mapping (soft hint shown in UI).
      if (!isXlsxFile) {
        autoMapFromCsv(selected);
      }
    },
    [autoMapFromCsv, defaultMapping]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0] ?? null;
      applySelectedFile(selected);
    },
    [applySelectedFile]
  );

  // TASK-413 (AC-1): drag-and-drop onto the file block. dragover must call
  // preventDefault() or the browser refuses to fire `drop` at all; dragleave
  // just clears the active-hover visual.
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragActive(false);
      const dropped = e.dataTransfer.files?.[0] ?? null;
      applySelectedFile(dropped);
    },
    [applySelectedFile]
  );

  const submitUpload = useCallback((overrides: Partial<UploadRequestOptions>) => {
    setResult(null);
    setError(null);
    setFieldErrors({});
    clearCompanyErrors();

    // KAN-725: a file wins over a link; without a file the link must be a
    // Google Sheets one (the backend re-checks the host strictly).
    const sheetUrl = options.sheetUrl.trim();
    if (!file && !sheetUrl) {
      setError('Выберите файл или укажите ссылку на Google-таблицу.');
      return;
    }
    if (!file && !isGoogleSheetUrl(sheetUrl)) {
      setError('Это не ссылка на Google-таблицу');
      return;
    }
    const limit = parseUploadLimit(options.limit);
    if (limit === null) {
      setError('Сколько загрузить: целое число от 1 или пусто (все).');
      return;
    }

    // Every required schema field must be mapped to a source column.
    const missing = schema.filter(
      (field) => field.required && !mapping[field.name]?.trim()
    );
    if (missing.length > 0) {
      const labels = missing.map((f) => f.label || f.name).join(', ');
      setError(`Не сопоставлены обязательные колонки: ${labels}.`);
      return;
    }

    // KAN-826: custom fields are checked like the backend does.
    if (!validateCompanyForm()) {
      setError('Исправьте ошибки в разделе "Свои поля".');
      return;
    }

    // Drop empty mappings before sending.
    const cleaned: ColumnMapping = {};
    Object.entries(mapping).forEach(([field, source]) => {
      if (source?.trim()) {
        cleaned[field] = source.trim();
      }
    });

    uploadMutation.mutate({
      file,
      mapping: cleaned,
      options: {
        limit,
        skipExistingClients: options.skipExistingClients,
        sheetUrl: file ? undefined : sheetUrl,
        ...companyRequestOptions,
        ...overrides,
      },
    });
  }, [
    file,
    mapping,
    options,
    schema,
    uploadMutation,
    validateCompanyForm,
    clearCompanyErrors,
    companyRequestOptions,
  ]);

  const handleSubmit = useCallback(() => submitUpload({}), [submitUpload]);
  // KAN-826: preview writes nothing; "Дополнить" from it is the real upload.
  const handlePreview = useCallback(
    () => submitUpload({ updateExisting: true, dryRun: true }),
    [submitUpload]
  );
  const handleRunUpdate = useCallback(
    () => submitUpload({ updateExisting: true }),
    [submitUpload]
  );

  return (
    <PageLayout
      data-testid="dataset-upload-page"
      title="Загрузка списка"
      subtitle={
        dataset ? (
          <span className="hidden sm:inline-flex items-center gap-2">
            {dataset.name}
            <CampaignKindBadge kind={dataset.kind} />
          </span>
        ) : undefined
      }
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Upload className="h-5 w-5 text-white" />
        </div>
      }
      backButton={{ onClick: () => navigate(`/clients/cold-calls/${id}`) }}
      breadcrumbs={[
        { label: 'Главная', href: '/' },
        { label: 'Контакты', href: '/clients' },
        { label: 'Холодные обзвоны', href: '/clients/cold-calls' },
        { label: 'Загрузка списка' },
      ]}
    >
      <div className="max-w-2xl space-y-6">
        {/* KAN-752: a Telegram list is previewed for its campaign operator */}
        {dataset && (
          <OutcomePreview
            kind={dataset.kind ?? 'voice'}
            ownerId={
              dataset.kind === 'telegram_outreach' ? dataset.owner ?? undefined : undefined
            }
          />
        )}

        {/* TASK-353 (B): file-format requirements, shown BEFORE upload so the
            client learns the rules up front instead of after a failed import. */}
        <div
          data-testid="upload-format-hint"
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3 text-sm text-blue-900"
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0" />
            <h3 className="text-base font-medium">Требования к файлу</h3>
          </div>

          {/* Columns list is schema-driven — render only once the schema is
              loaded, otherwise it's an empty bullet list. */}
          {schema.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Колонки:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {schema.map((field) => (
                  <li key={field.name}>
                    {field.label || field.name} —{' '}
                    {field.required ? (
                      <span className="font-medium">обязательно</span>
                    ) : (
                      'опционально'
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="list-disc pl-5 space-y-0.5">
            <li>
              Форматы: CSV (разделитель — запятая или точка с запятой, кодировка
              UTF-8) или XLSX (первый лист).
            </li>
            <li>Первая строка файла — заголовки колонок.</li>
          </ul>

          {/* Example block also depends on the schema (headers + sample row). */}
          {schema.length > 0 && (
            <div>
              <p className="mb-1 font-medium">Пример:</p>
              <pre className="overflow-x-auto rounded bg-white/70 border border-blue-200 p-2 text-xs text-gray-800">
                {previewLines.join('\n')}
              </pre>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            data-testid="download-template-button"
            onClick={handleDownloadTemplate}
            disabled={schema.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Скачать шаблон
          </Button>
        </div>

        {/* File picker / DropZone (TASK-413 AC-1): the native <input> stays
            (click still opens the system dialog) — the surrounding container
            additionally accepts a dragged-and-dropped file through the same
            `applySelectedFile` path as a picked one. */}
        <div
          data-testid="upload-dropzone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'bg-white border-2 border-dashed rounded-lg p-4 space-y-2 transition-colors',
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-200'
          )}
        >
          <Label htmlFor="upload-file">Файл CSV / Excel</Label>
          <p className="text-xs text-gray-400">
            Перетащите файл сюда или выберите вручную
          </p>
          <Input
            id="upload-file"
            data-testid="upload-file-input"
            type="file"
            accept=".csv,.xlsx"
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          {file && (
            <p className="text-sm text-gray-500">Выбран файл: {file.name}</p>
          )}
          <UploadSheetUrlInput
            value={options}
            onChange={setOptions}
            recent={recentSheets}
            onPickRecent={handlePickRecent}
          />
          {/* TASK-353/TASK-413 (AC-2): XLSX cannot be auto-mapped on the front —
              explain that «Сопоставление колонок» is already prefilled with
              defaults (TASK-370) and only needs a check, not a fresh guess. */}
          {isXlsx && !headersData?.headers.length && (
            <p
              data-testid="upload-xlsx-hint"
              className="text-sm text-blue-700"
            >
              Поля «Сопоставление колонок» ниже уже заполнены названиями по
              умолчанию — проверьте, что они совпадают с заголовками колонок
              в вашем файле, и поправьте несовпадающие вручную.
            </p>
          )}
        </div>

        {/* Schema-driven column mapping (KAN-727: selects of real headers) */}
        <ColumnMappingFields
          schema={schema}
          mapping={mapping}
          onChange={handleMappingChange}
          headers={headersData}
          headersError={headersState.error}
          headersLoading={headersState.isLoading}
          fieldErrors={fieldErrors}
        />

        <UploadCompanyFields
          value={companyForm.companyFields}
          onChange={companyForm.setCompanyField}
          headers={headerNames}
          errors={companyForm.companyServerErrors}
        />

        <UploadExtraFields
          rows={companyForm.extraRows}
          headers={headerNames}
          onAdd={companyForm.addExtraRow}
          onChange={companyForm.updateExtraRow}
          onRemove={companyForm.removeExtraRow}
          rowErrors={companyForm.rowErrors}
          errors={companyForm.extraServerErrors}
        />

        <UploadOptions value={options} onChange={setOptions} />

        <UploadUpdateExisting
          checked={companyForm.updateExisting}
          onChange={companyForm.setUpdateExisting}
          job={updateJob.view}
          busy={uploadMutation.isPending || updateJob.isRunning}
          onPreview={handlePreview}
          onRun={handleRunUpdate}
        />

        {error && (
          <div
            data-testid="upload-error"
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* TASK-338 AC-5: honest import result — a 0-created import is NOT a
            success. It renders an amber warning with the same counters plus
            hints on the usual causes (CSV format, column mapping). */}
        {result && result.created === 0 && (
          <div
            data-testid="upload-result-warning"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          >
            <h3 className="font-medium mb-2">
              Не импортировано ни одной записи
            </h3>
            <p data-testid="upload-summary" className="mb-2">
              {formatUploadSummary(result)}
            </p>
            <ul className="space-y-1 mb-3">
              <li>
                Создано записей: <strong>{result.created}</strong>
              </li>
              <li>
                Дубликатов: <strong>{result.duplicates}</strong>
              </li>
              <li>
                Ошибок: <strong>{result.invalid}</strong>
              </li>
            </ul>
            <UploadUpdateCounters result={result} />
            <p className="font-medium mb-1">Что проверить:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                CSV: первая строка — заголовки колонок, разделитель — запятая
                или точка с запятой; надёжнее сохранить файл как .xlsx.
              </li>
              <li>
                Сопоставление колонок: названия должны совпадать с заголовками
                в файле.
              </li>
            </ul>
          </div>
        )}

        {result && result.created > 0 && (
          <div
            data-testid="upload-result"
            className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800"
          >
            <h3 className="font-medium mb-2">Импорт завершён</h3>
            <p data-testid="upload-summary" className="mb-2">
              {formatUploadSummary(result)}
            </p>
            <ul className="space-y-1">
              <li>
                Создано записей: <strong>{result.created}</strong>
              </li>
              <li>
                Дубликатов: <strong>{result.duplicates}</strong>
              </li>
              <li>
                Ошибок: <strong>{result.invalid}</strong>
              </li>
            </ul>
            <UploadUpdateCounters result={result} />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            data-testid="upload-submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleSubmit}
            disabled={uploadMutation.isPending || updateJob.isRunning}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Загрузить
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}

export default DatasetUploadPage;
